import { normalizeView } from "./steps/photoViewNames";

export type PreviewMedia = string | {
  blobImage?: string;
  mediaURL?: string;
  mediaUrl?: string;
  kind?: string;
  type?: string;
  source?: string;
  lat?: number;
  lng?: number;
};

export const isShownMedia = (ele: any): boolean =>
  !(ele?.show === false || ele?.show === 0 || ele?.show === "0");

export const toPreviewMedia = (
  ele: any
): {
  blobImage: string;
  mediaURL: string;
  kind?: string;
  type?: string;
  source?: string;
  lat?: number;
  lng?: number;
} | null => {
  if (!ele) {
    return null;
  }
  if (typeof ele === "string") {
    return ele ? { blobImage: ele, mediaURL: ele } : null;
  }
  const url = String(ele.mediaURL || ele.mediaUrl || ele.blobImage || "");
  if (!url) {
    return null;
  }
  return {
    blobImage: url,
    mediaURL: url,
    kind: ele.kind,
    type: ele.type,
    source: ele.source,
    lat: ele.lat,
    lng: ele.lng,
  };
};

export const previewMediaUrl = (item: PreviewMedia | null | undefined): string => {
  if (!item) {
    return "";
  }
  if (typeof item === "string") {
    return item;
  }
  return String(item.blobImage || item.mediaURL || item.mediaUrl || "");
};

/** Grid checkboxes index only rows with a preview URL. Map those back to the source array. */
export const mediaItemsAtVisibleIndexes = (
  items: any[] | null | undefined,
  indexes: number[]
): any[] => {
  const visible = (Array.isArray(items) ? items : []).filter((item) => Boolean(previewMediaUrl(item)));
  return indexes.map((i) => visible[i]).filter(Boolean);
};

export const dropMediaItems = (items: any[] | null | undefined, remove: any[]): any[] => {
  const rows = Array.isArray(items) ? items : [];
  const refs = new Set(remove);
  const ids = new Set(
    remove.map((item) => String(item?.mediaId || "").trim()).filter(Boolean)
  );
  return rows.filter((item) => {
    if (refs.has(item)) {
      return false;
    }
    const id = String(item?.mediaId || "").trim();
    return !(id && ids.has(id));
  });
};

/** A durable HKP media reference ("hkp-media:<uuid>"), as the server stores and sends it back when it
 * cannot sign a link (media service off or the photo not ready). */
export const isMediaReference = (url: string | null | undefined): boolean =>
  /^hkp-media:[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/.test(String(url || "").trim());

/**
 * Blob and data URLs die after refresh; only http(s) files and HKP media references belong in product
 * records. Dropping a reference here erased the photo on the next save.
 */
export const isPersistedMediaUrl = (url: string | null | undefined): boolean => {
  const value = String(url || "").trim();
  if (!value) {
    return false;
  }
  const lower = value.toLowerCase();
  if (lower.startsWith("blob:") || lower.startsWith("data:")) {
    return false;
  }
  return /^https?:\/\//i.test(value) || isMediaReference(value);
};

export const persistableMediaUrl = (url: string | null | undefined): string =>
  isPersistedMediaUrl(url) ? String(url).trim() : "";

export const mediaUrlKey = (url: string | null | undefined): string => {
  const value = persistableMediaUrl(url);
  return value ? value.split("?")[0] : "";
};

/** Saved http(s) URL on a row, even when blobImage is still a local preview. */
export const itemPersistableUrl = (item: any): string => {
  if (item == null) {
    return "";
  }
  if (typeof item === "string") {
    return persistableMediaUrl(item);
  }
  return (
    persistableMediaUrl(item.mediaURL) ||
    persistableMediaUrl(item.mediaUrl) ||
    persistableMediaUrl(item.blobImage)
  );
};

export const isLocalMediaItem = (item: any): boolean => {
  if (itemPersistableUrl(item)) {
    return false;
  }
  if (!item || typeof item !== "object") {
    return false;
  }
  if (item._local === true) {
    return true;
  }
  const lower = String(previewMediaUrl(item) || "").toLowerCase();
  return lower.startsWith("blob:") || lower.startsWith("data:");
};

export const shouldShowMediaUploadSpinner = (loader: boolean, item: any): boolean =>
  Boolean(loader) && isLocalMediaItem(item);

export const isUploadableFile = (file: unknown): file is File =>
  typeof File !== "undefined" && file instanceof File && file.size > 0;

const flattenFiles = (value: unknown): File[] => {
  if (isUploadableFile(value)) {
    return [value];
  }
  if (Array.isArray(value)) {
    return value.flatMap(flattenFiles);
  }
  if (value && typeof value === "object" && typeof (value as FileList).length === "number") {
    return Array.from(value as FileList).flatMap(flattenFiles);
  }
  return [];
};

const fileKey = (file: File) => `${file.name}:${file.size}:${file.lastModified}`;

/** Session File objects only — skip https preview rows and nested picker arrays. */
export const collectUniqueFiles = (
  existing: unknown,
  incoming?: ArrayLike<File> | File[] | FileList | null
): File[] => {
  const next = [...flattenFiles(existing), ...flattenFiles(incoming)];
  return next.filter((file, i, self) => i === self.findIndex((t) => fileKey(t) === fileKey(file)));
};

const revokeLocalBlob = (item: any) => {
  const url = previewMediaUrl(item);
  if (!item?._local || !String(url).startsWith("blob:")) {
    return;
  }
  try {
    URL.revokeObjectURL(url);
  } catch {
    // ignore
  }
};

/** Keep already-saved https rows; rebuild local previews from newly picked Files only. */
export const mergePersistedWithLocalPreviews = (
  existing: any[] | null | undefined,
  newFiles: File[],
  extra?: { pin?: boolean; uploadStatus?: string; view?: string | null }
): any[] => {
  const rows = Array.isArray(existing) ? existing : [];
  const files = collectUniqueFiles([], newFiles);
  // A picker hands back every file picked so far: keep the rows already shown for those (their
  // upload, view and pin), and only add rows for the files picked just now.
  const wanted = new Set(files.map(fileKey));
  const shown = new Map<string, any>();
  rows.filter(isLocalMediaItem).forEach((row) => {
    if (row?.fileKey && wanted.has(row.fileKey) && !shown.has(row.fileKey)) {
      shown.set(row.fileKey, row);
    } else {
      revokeLocalBlob(row);
    }
  });
  const persisted = rows.filter((ele) => Boolean(itemPersistableUrl(ele)));
  const locals = files.map(
    (file) =>
      shown.get(fileKey(file)) || {
        type: file.type,
        blobImage: URL.createObjectURL(file),
        fileName: file.name,
        fileKey: fileKey(file),
        show: true,
        pin: extra?.pin === true,
        _local: true,
        ...(extra?.uploadStatus ? { uploadStatus: extra.uploadStatus, uploadProgress: 0 } : {}),
        ...(normalizeView(extra?.view) ? { view: normalizeView(extra?.view) } : {}),
      }
  );
  return [...persisted, ...locals];
};

const isPinnedMedia = (ele: any): boolean =>
  ele?.pin === true || ele?.pin === 1 || ele?.pin === "1";

/** Shown, pinned-first https photo rows the product list can actually render, with their copy links. */
export const pickProductListPreviewRows = (media: any[] | null | undefined): any[] => {
  const rows = Array.isArray(media) ? media : [];
  const shown = rows.filter(isShownMedia);
  const ordered = [
    ...shown.filter(isPinnedMedia),
    ...shown,
  ];
  const picked: any[] = [];
  const seen = new Set<string>();
  for (const row of ordered) {
    const url = persistableMediaUrl(row?.mediaURL || row?.mediaUrl || row?.blobImage);
    // A reference the server could not sign is saved but not viewable: let a loadable photo show instead.
    if (!url || isMediaReference(url) || seen.has(url)) {
      continue;
    }
    seen.add(url);
    if (isPreviewVideo(row) || isPreviewVideo(url)) {
      continue;
    }
    picked.push(typeof row === "string" ? url : { ...row, mediaURL: url });
  }
  return picked;
};

/** Shown, pinned-first https photos the product list can actually render. */
export const pickProductListPreviewUrls = (media: any[] | null | undefined): string[] =>
  pickProductListPreviewRows(media).map((row) => (typeof row === "string" ? row : row.mediaURL));

/** Smaller copies the list API adds beside `mediaURL` for old photos (320 px and 480 px). */
const LIST_COPY_FIELD = { THUMBNAIL: "mediaUrlThumb", PRODUCT_CARD: "mediaUrlCard" } as const;

/**
 * The photo a list row shows. With a `preset`, a row the API sent a smaller copy for gives that copy,
 * so a 68 px thumbnail does not download the 800 px photo the preview opens.
 */
export const pickProductListThumbUrl = (
  media: any[] | null | undefined,
  preset?: keyof typeof LIST_COPY_FIELD
): string => {
  const main = pickProductListPreviewUrls(media)[0] || "";
  if (!main || !preset) {
    return main;
  }
  const row = (Array.isArray(media) ? media : []).find(
    (item) => persistableMediaUrl(item?.mediaURL || item?.mediaUrl || item?.blobImage) === main
  );
  const small = persistableMediaUrl(row?.[LIST_COPY_FIELD[preset]]);
  return small && !isMediaReference(small) ? small : main;
};

/**
 * The cover the editor previews: the list's own pick (shown, pinned first, no videos), or, while a
 * photo is still uploading, the same pick among the local previews.
 */
export const pickEditorCoverUrl = (media: any[] | null | undefined): string => {
  const listed = pickProductListThumbUrl(media);
  if (listed) return listed;
  const shown = (Array.isArray(media) ? media : []).filter((row) => isShownMedia(row) && !isPreviewVideo(row));
  const local = [...shown.filter(isPinnedMedia), ...shown].find((row) => row?.blobImage);
  return local?.blobImage || "";
};

const flag01 = (value: any): 0 | 1 =>
  value === true || value === 1 || value === "1" ? 1 : 0;

const shown01 = (row: any): 0 | 1 => (isShownMedia(row) ? 1 : 0);

/** A row's photo view for the save; videos, the VIDEO slot and unknown values are never sent as a view. */
const rowPhotoView = (row: any): string | undefined => (isPreviewVideo(row) ? undefined : normalizeView(row?.view) || undefined);

export const persistableThumbnailRows = (rows: any[] | null | undefined): any[] => {
  const out: any[] = [];
  const seen = new Set<string>();
  for (const row of Array.isArray(rows) ? rows : []) {
    const mediaId = String(row?.mediaId || "").trim();
    const url = mediaUrlKey(itemPersistableUrl(row));
    if (!url) {
      continue;
    }
    const key = mediaId ? `id:${mediaId}` : url;
    if (seen.has(key)) {
      continue;
    }
    seen.add(key);
    out.push({
      ...(mediaId ? { mediaId } : {}),
      mediaUrl: url,
      show: shown01(row),
      pin: flag01(row?.pin),
      ...(rowPhotoView(row) ? { view: rowPhotoView(row) } : {}),
    });
  }
  return out;
};

/** Last-mile PUT/POST media: ints only. Boolean show/pin is a 400 from UpdateProductDTO. */
export const mediaForProductWrite = (
  rows: any[] | null | undefined,
  withPin = false
): Record<string, unknown>[] =>
  (Array.isArray(rows) ? rows : []).map((row) => {
    const item: Record<string, unknown> = {
      mediaUrl: String(row?.mediaUrl || row?.mediaURL || "").split("?")[0],
      show: shown01(row),
    };
    if (withPin) {
      item.pin = flag01(row?.pin);
      if (row?.view) {
        item.view = String(row.view).toUpperCase();
      }
    }
    const lat = Number(row?.lat);
    const lng = Number(row?.lng);
    if (Number.isFinite(lat) && Number.isFinite(lng)) {
      item.lat = lat;
      item.lng = lng;
      const accuracy = Number(row?.accuracyM);
      if (Number.isFinite(accuracy)) {
        item.accuracyM = accuracy;
      }
      if (row?.source) {
        item.source = String(row.source);
      }
      if (row?.kind) {
        item.kind = String(row.kind);
      }
      if (row?.uploadByName) {
        item.uploadByName = String(row.uploadByName);
      }
    }
    return item;
  });

export const persistableProcessMediaRows = (baseImages: any[] | null | undefined): any[] => {
  const rows: any[] = [];
  const seen = new Set<string>();
  for (const b of Array.isArray(baseImages) ? baseImages : []) {
    const url = mediaUrlKey(itemPersistableUrl(b));
    if (!url || seen.has(url)) {
      continue;
    }
    seen.add(url);
    rows.push({
      mediaUrl: url,
      show: shown01(b),
      ...(b?.lat != null && b?.lng != null
        ? {
            lat: Number(b.lat),
            lng: Number(b.lng),
            accuracyM: b?.accuracyM != null ? Number(b.accuracyM) : undefined,
            source: b?.source || undefined,
            kind: b?.kind || undefined,
            uploadByName: b?.uploadByName || undefined,
          }
        : {}),
    });
  }
  return rows.length ? rows : [{ mediaUrl: "", show: 0 }];
};

export const replaceLocalPreviewsWithUploaded = (
  existing: any[] | null | undefined,
  uploaded: { status?: number; statusCode?: number; url?: string }[],
  files: File[]
): any[] => {
  const kept: any[] = [];
  const seen = new Set<string>();
  const pushPersisted = (row: any, url: string) => {
    const key = mediaUrlKey(url);
    if (!key || seen.has(key)) {
      return;
    }
    seen.add(key);
    kept.push({ ...row, blobImage: key, _local: false });
  };
  (Array.isArray(existing) ? existing : []).forEach((row) => {
    const url = itemPersistableUrl(row);
    if (url) {
      pushPersisted(row, url);
    }
  });
  const succeeded = new Set<string>();
  files.forEach((file, i) => {
    const row = uploaded[i] || {};
    const ok = row.status === 200 || row.statusCode === 200;
    const url = persistableMediaUrl(row.url);
    if (ok && url) {
      succeeded.add(file.name);
      pushPersisted({ type: file.type, fileName: file.name, show: true }, url);
    }
  });
  (Array.isArray(existing) ? existing : []).forEach((row) => {
    if (isLocalMediaItem(row) && row?.fileName && !succeeded.has(row.fileName)) {
      kept.push(row);
    }
  });
  return kept;
};

const pathOf = (url: string): string => {
  if (!url) {
    return "";
  }
  try {
    return new URL(url).pathname.toLowerCase();
  } catch {
    return url.split("?")[0].toLowerCase();
  }
};

export const isPreviewVideo = (item: PreviewMedia | null | undefined): boolean => {
  if (item && typeof item === "object") {
    const kind = String(item.kind || "").toUpperCase();
    if (kind === "VIDEO") return true;
    if (kind === "PHOTO") return false;
    const type = String(item.type || "").toLowerCase();
    if (type.startsWith("video/")) return true;
    if (type.startsWith("image/")) return false;
  }
  return /\.(mp4|m4v|webm|ogg|mov|avi|mkv|wmv|flv)(\?|$)/i.test(pathOf(previewMediaUrl(item)));
};

/** Safari/iOS often skip the first frame unless a media fragment is set. */
export const videoSrcWithPreviewFrame = (url: string | null | undefined): string => {
  const value = String(url || "").trim();
  if (!value || value.includes("#")) return value;
  return `${value}#t=0.1`;
};

export const isPreviewImage = (item: PreviewMedia | null | undefined): boolean => {
  if (isPreviewVideo(item)) {
    return false;
  }
  if (item && typeof item === "object") {
    const kind = String(item.kind || "").toUpperCase();
    if (kind === "PHOTO") return true;
    const type = String(item.type || "").toLowerCase();
    if (type.startsWith("image/")) return true;
  }
  const path = pathOf(previewMediaUrl(item));
  return /\.(jpg|jpeg|png|bmp|gif|webp|heic)(\?|$)/i.test(path);
};

export const isPreviewDoc = (item: PreviewMedia | null | undefined): boolean => {
  if (item && typeof item === "object") {
    const type = String(item.type || "").toLowerCase();
    if (type.includes("pdf") || type.includes("word") || type.includes("document")) {
      return true;
    }
  }
  return /\.(pdf|doc|docx)(\?|$)/i.test(pathOf(previewMediaUrl(item)));
};

export const pauseMediaElement = (el: HTMLMediaElement | null | undefined) => {
  if (!el) return;
  el.pause();
  try {
    el.currentTime = 0;
  } catch {
    // Some browsers reject seeking before metadata is ready.
  }
};
